/**
 * AcceptRejectCard — Day-3 §3.3 SYSTEM-AGENT-HELPERS-DESIGN.md.
 *
 * Renders the stack of canvas-mutation tool calls the helper has
 * proposed and lets the user Accept or Reject each (or all at once).
 *
 * Spec §3.3 design:
 *
 *   ┌──────────────────────────────────────────┐
 *   │ Argus Architect suggests:                │
 *   │   • Name → "Equipment Health Monitor"    │  [Accept] [Reject]
 *   │   • Add tool: iq.asset.search            │  [Accept] [Reject]
 *   │   • Add tool: iq.telemetry.getCurrent    │  [Accept] [Reject]
 *   │   • Set autonomy: assisted               │  [Accept] [Reject]
 *   │                                          │
 *   │   [Accept all]   [Reject all]            │
 *   └──────────────────────────────────────────┘
 *
 * §3.3 nag-fatigue mitigation: the primary action is "Apply draft
 * (N changes)" — single click — with per-row Reject as the escape.
 *
 * §3.3 hallucinated-slug mitigation: a card whose mutation references
 * a slug not in the snapshot can be marked invalid by the caller via
 * `useHelperChat#markMutationInvalid`; the Accept button on that card
 * is disabled with an "INVALID — slug not in catalog" badge.
 *
 * The component is presentation-only — `applyMutation` and
 * `rejectMutation` are caller-supplied callbacks that interpret each
 * mutation against the caller's canvas state. This keeps the same
 * component reusable across all five helper kinds (architect,
 * function_helper, tool_helper, skill_helper, prompt_helper).
 */

import { Check, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PendingMutation } from '@/lib/use-studio-helper';
import { cn } from '@/lib/utils';

interface AcceptRejectCardProps {
  /** The pending mutations the helper proposed. Empty → render nothing. */
  mutations: PendingMutation[];
  /** Helper display label used in the card header. */
  helperLabel?: string;
  /** Called when the user clicks Accept on a row. */
  applyMutation: (m: PendingMutation) => void;
  /** Called when the user clicks Reject on a row. */
  rejectMutation: (m: PendingMutation) => void;
  /** Called when the user clicks "Apply draft" — accept all. */
  applyAll?: () => void;
  /** Called when the user clicks "Reject all". */
  rejectAll?: () => void;
  className?: string;
}

/* Map mutation tool names → short human label rendered in the card. */
const MUTATION_LABELS: Record<string, (args: Record<string, unknown>) => string> = {
  set_identity: (a) =>
    `Identity → ${[a.name, a.category && `${a.category}`].filter(Boolean).join(' · ')}`,
  set_system_prompt: (a) =>
    `System prompt (${String(a.prompt ?? '').length} chars)`,
  set_model: (a) => `Model → ${a.brand}`,
  add_tool: (a) => `Add tool → ${a.slug}`,
  remove_tool: (a) => `Remove tool → ${a.slug}`,
  add_skill: (a) => `Add skill → ${a.slug}`,
  remove_skill: (a) => `Remove skill → ${a.slug}`,
  add_function: (a) => `Add function → ${a.slug}`,
  remove_function: (a) => `Remove function → ${a.slug}`,
  add_knowledge: (a) => `Add knowledge → ${a.collection}`,
  remove_knowledge: (a) => `Remove knowledge → ${a.collection}`,
  set_guardrail_profile: (a) => `Guardrails → ${a.profile}`,
  set_forbidden_topics: (a) =>
    `Forbidden topics → ${Array.isArray(a.topics) ? a.topics.length : 0} item(s)`,
  set_autonomy: (a) => `Autonomy → ${a.level}`,
  set_triggers: (a) =>
    `Triggers → ${Array.isArray(a.modes) ? (a.modes as string[]).join(', ') : ''}`,
  set_schedule: (a) => `Schedule → ${a.cron}`,
  // function_helper
  set_metadata: (a) =>
    `Metadata → ${[a.name, a.runtime && `[${a.runtime}]`].filter(Boolean).join(' ')}`,
  set_code_body: (a) => `Code body (${String(a.code ?? '').length} chars)`,
  set_input_schema: () => `Input schema`,
  set_output_schema: () => `Output schema`,
  set_policy: (a) =>
    `Policy → ${a.approval}${Array.isArray(a.sideEffects) ? ` [${(a.sideEffects as string[]).join(', ')}]` : ''}`,
  add_valve: (a) => `Add valve → ${a.name}`,
  add_test_case: (a) => `Add test case → ${a.name}`,
  // tool_helper
  set_http_manifest: (a) =>
    `HTTP manifest -> ${[a.method, a.url ?? a.baseUrl].filter(Boolean).join(' ')}`,
  set_source: (a) => `Source → ${a.sourceType}`,
  // skill_helper
  set_instructions: (a) => `Instructions (${String(a.instructionsMd ?? '').length} chars)`,
  add_tool_dep: (a) => `Add tool dep → ${a.slug}`,
  remove_tool_dep: (a) => `Remove tool dep → ${a.slug}`,
  set_variables: (a) =>
    `Variables → ${Array.isArray(a.variables) ? a.variables.length : 0} item(s)`,
  set_guardrails: (a) =>
    `Guardrails → ${Array.isArray(a.guardrails) ? a.guardrails.length : 0} rule(s)`,
  // prompt_helper
  set_template_body: (a) => `Template body (${String(a.templateBody ?? '').length} chars)`,
  set_persona: (a) => `Persona → ${a.persona}`,
  set_tone: (a) => `Tone → ${a.tone}`,
};

/** Pure label resolver — exported for unit tests. */
export function _labelForMutation(m: { toolName: string; args: Record<string, unknown> }): string {
  const fn = MUTATION_LABELS[m.toolName];
  if (fn) return fn(m.args);
  // Defensive fallback — unknown tool name (model emitted something
  // not in our subset, or a future Wave 4 op).
  return `${m.toolName}(${JSON.stringify(m.args).slice(0, 80)}…)`;
}

function labelForMutation(m: PendingMutation): string {
  return _labelForMutation(m);
}

export function _isMutationApplicable(
  m: Pick<PendingMutation, 'confirmed' | 'turnComplete' | 'invalidReason'>,
): boolean {
  return m.confirmed && m.turnComplete && !m.invalidReason;
}

export function AcceptRejectCard({
  mutations,
  helperLabel,
  applyMutation,
  rejectMutation,
  applyAll,
  rejectAll,
  className,
}: AcceptRejectCardProps) {
  if (mutations.length === 0) return null;

  const validCount = mutations.filter(_isMutationApplicable).length;
  const pendingCount = mutations.filter((m) => (!m.confirmed || !m.turnComplete) && !m.invalidReason).length;
  const invalidCount = mutations.filter((m) => !!m.invalidReason).length;
  const allInvalid = validCount === 0;

  return (
    <div
      className={cn(
        'rounded-lg border border-cyan-200 bg-cyan-50/40 dark:border-cyan-900 dark:bg-cyan-950/20 p-3 shadow-sm',
        className,
      )}
      data-testid="helper-accept-reject-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-cyan-900 dark:text-cyan-100">
          {helperLabel ?? 'Argus'} suggests{' '}
          <span className="text-cyan-700 dark:text-cyan-300">
            {validCount} change{validCount === 1 ? '' : 's'}
            {pendingCount > 0 && (
              <span className="text-muted-foreground">
                {' '}
                · {pendingCount} validating
              </span>
            )}
            {invalidCount > 0 && (
              <span className="text-amber-700 dark:text-amber-300">
                {' '}
                · {invalidCount} invalid
              </span>
            )}
          </span>
        </div>
      </div>

      <ul className="space-y-1.5 mb-3">
        {mutations.map((m) => (
          <li
            key={m.callId}
            className={cn(
              'flex items-center justify-between gap-2 rounded border bg-background px-2 py-1.5 text-sm',
              m.invalidReason
                ? 'border-amber-300 bg-amber-50/30 dark:border-amber-900 dark:bg-amber-950/20'
                : 'border-cyan-100 dark:border-cyan-900',
            )}
            data-testid="helper-mutation-row"
            data-tool-name={m.toolName}
            data-call-id={m.callId}
          >
            <div className="min-w-0 flex-1 truncate">
              <span className="font-mono text-xs text-muted-foreground mr-1">
                {m.toolName}
              </span>
              <span className="font-medium">{labelForMutation(m)}</span>
              {m.invalidReason && (
                <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="size-3 mr-1" />
                  {m.invalidReason}
                </Badge>
              )}
              {(!m.confirmed || !m.turnComplete) && !m.invalidReason && (
                <span className="ml-2 text-xs text-muted-foreground italic">
                  {!m.confirmed ? 'validating…' : 'finalizing…'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                disabled={!m.confirmed || !m.turnComplete || !!m.invalidReason}
                onClick={() => applyMutation(m)}
                aria-label={`Accept ${m.toolName}`}
                title={
                  m.invalidReason
                    ? m.invalidReason
                    : !m.confirmed
                      ? 'Waiting for helper validation'
                      : !m.turnComplete
                        ? 'Waiting for final helper validation'
                      : `Accept ${m.toolName}`
                }
              >
                <Check className="size-4 mr-1" /> Accept
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-rose-700 hover:text-rose-900 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                onClick={() => rejectMutation(m)}
                aria-label={`Reject ${m.toolName}`}
                title={`Reject ${m.toolName}`}
              >
                <X className="size-4 mr-1" /> Reject
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {mutations.length > 1 && (
        <div className="flex items-center gap-2 pt-2 border-t border-cyan-100 dark:border-cyan-900">
          <Button
            size="sm"
            onClick={applyAll}
            disabled={allInvalid}
            title={allInvalid ? 'No valid suggestions to apply' : `Apply ${validCount} changes`}
          >
            <Check className="size-4 mr-1" />
            Apply draft ({validCount} change{validCount === 1 ? '' : 's'})
          </Button>
          <Button size="sm" variant="ghost" onClick={rejectAll}>
            <X className="size-4 mr-1" />
            Reject all
          </Button>
        </div>
      )}
    </div>
  );
}
